import { defineStore } from 'pinia'
import { useAuthStore } from '@/features/account/session'
import apiService from '@/api'
import { sortConversations } from '@/utils/sorting'
import type { ConversationCreateData } from '@/api/contract'
import type { Conversation } from '@/types/domain'

interface ConversationsState {
  conversations: Conversation[]
  loading: boolean
  error: string | null
  lastFetched: number | null
  cacheDuration: number
  accountId: string | null
  generation: number
}

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback

export const useConversationsStore = defineStore('conversations', {
  state: (): ConversationsState => ({
    conversations: [],
    loading: false,
    error: null,
    lastFetched: null,
    cacheDuration: 5 * 60 * 1000,
    accountId: null,
    generation: 0,
  }),

  getters: {
    getConversationById: (state) => (id: number) =>
      state.conversations.find((conversation) => conversation.id === id),
    getUserConversations: (state) => (userId: string) =>
      sortConversations(state.conversations.filter((conversation) => conversation.user_id === userId)),
    getConversationsByCharacter: (state) => (characterId: number) =>
      state.conversations.filter((conversation) => conversation.character_id === characterId),
  },

  actions: {
    setAccount(id: string | null) {
      if (this.accountId === id) return
      this.accountId = id
      this.generation++
      this.conversations = []
      this.loading = false
      this.error = null
      this.lastFetched = null
    },

    assertLifetime(generation: number) {
      if (generation !== this.generation || this.accountId !== useAuthStore().userId) {
        throw new Error('The active account changed. Please try again.')
      }
    },

    async fetchConversations(forceRefresh = false) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      const now = Date.now()
      if (!forceRefresh && this.lastFetched && now - this.lastFetched < this.cacheDuration) {
        return this.conversations
      }
      this.loading = true
      this.error = null
      try {
        const response = await apiService.getConversations()
        this.assertLifetime(generation)
        this.conversations = response.conversations || []
        this.lastFetched = now
        return this.conversations
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = errorMessage(error, 'Failed to fetch conversations')
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    async fetchConversation(id: number) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.loading = true
      this.error = null
      try {
        const response = await apiService.getConversation(id)
        this.assertLifetime(generation)
        const conversation = response.conversation
        const index = this.conversations.findIndex((item) => item.id === conversation.id)
        if (index === -1) this.conversations.push(conversation)
        else this.conversations[index] = conversation
        return conversation
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = errorMessage(error, 'Failed to fetch conversation')
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    async createConversation(conversationData: ConversationCreateData) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.error = null
      try {
        const response = await apiService.createConversation(conversationData)
        this.assertLifetime(generation)
        const conversation = response
        this.conversations.push(conversation)
        return conversation
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = errorMessage(error, 'Failed to create conversation')
        throw error
      }
    },

    async deleteConversation(id: number) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.error = null
      try {
        const response = await apiService.deleteConversation(id)
        this.assertLifetime(generation)
        this.conversations = this.conversations.filter((conversation) => conversation.id !== id)
        return response
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = errorMessage(error, 'Failed to delete conversation')
        throw error
      }
    },

    async updateConversationTitle(id: number, title: string) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.error = null
      try {
        const response = await apiService.updateConversation(id, { title })
        this.assertLifetime(generation)
        const index = this.conversations.findIndex((conversation) => conversation.id === id)
        if (index !== -1) this.conversations[index] = { ...this.conversations[index], title }
        return response.conversation
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = errorMessage(error, 'Failed to update conversation title')
        throw error
      }
    },

    reconcileConversation(conversation: Conversation, generation: number) {
      if (generation !== this.generation || this.accountId !== useAuthStore().userId) return
      const index = this.conversations.findIndex((item) => item.id === conversation.id)
      if (index === -1) this.conversations.push(conversation)
      else this.conversations[index] = conversation
    },
  },
})
